// P01 Profit Leakage & Margin Pressure Analyzer + composite case file.
import { useMemo, useState } from "react";
import { inr } from "./engines";
import { Field, ResultRow, Note, ToolSection, Waterfall, useNumbers } from "./ui";
import { track } from "@/lib/analytics";

const pctOf = (rev, p) => rev * (Number(p) || 0) / 100;

function bridge(v) {
  const revA = Number(v.revA) || 0, revB = Number(v.revB) || 0;
  const line = (prefix, key) => ({ a: pctOf(revA, v[`${key}A`]), b: pctOf(revB, v[`${key}B`]), label: prefix });
  const lines = [
    line("COGS", "cogs"), line("Freight & logistics", "freight"), line("Returns & claims", "returns"),
    line("Channel / marketplace fees", "fees"), line("Discounts, schemes & rebates", "schemes"),
  ];
  const contribA = revA - lines.reduce((s, l) => s + l.a, 0);
  const contribB = revB - lines.reduce((s, l) => s + l.b, 0);
  // split each cost line into rate effect (true margin loss) vs volume effect (cost that
  // grew only because revenue grew) — rank leaks by rate effect so growth isn't a "leak"
  const leaks = lines.map((l) => {
    const rateA = revA > 0 ? (l.a / revA) * 100 : 0;
    const rateB = revB > 0 ? (l.b / revB) * 100 : 0;
    return {
      ...l,
      delta: l.b - l.a,
      rateEffect: ((rateB - rateA) / 100) * revB,
      volumeEffect: (rateA / 100) * (revB - revA),
    };
  }).sort((a, b) => b.rateEffect - a.rateEffect);
  const wf = [
    { label: `Revenue (${v.labelA} → ${v.labelB})`, value: revB - revA, display: `${inr(revA)} → ${inr(revB)}` },
    ...lines.map((l) => ({ label: l.label, value: -(l.b - l.a), display: inr(l.b - l.a) })),
    { label: "Contribution change", value: contribB - contribA, total: true, display: inr(contribB - contribA) },
  ];
  return { revA, revB, contribA, contribB, leaks, wf,
    pctA: revA > 0 ? (contribA / revA) * 100 : 0, pctB: revB > 0 ? (contribB / revB) * 100 : 0 };
}

export function ToolP01() {
  const [v, set] = useNumbers({
    labelA: "Last year", labelB: "This year",
    revA: 10000000, revB: 12000000,
    cogsA: 62, cogsB: 64, freightA: 3, freightB: 3.5, returnsA: 1, returnsB: 2,
    feesA: 2, feesB: 4, schemesA: 4, schemesB: 6,
  });
  const r = useMemo(() => bridge(v), [v]);
  return (
    <div>
      <div className="grid md:grid-cols-2 gap-10">
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Revenue — last year" prefix="₹" value={v.revA} onChange={set("revA")} testid="p01-rev-a" />
            <Field label="Revenue — this year" prefix="₹" value={v.revB} onChange={set("revB")} testid="p01-rev-b" />
          </div>
          {[
            ["cogs", "COGS"], ["freight", "Freight & logistics"], ["returns", "Returns & claims"],
            ["fees", "Channel / marketplace fees"], ["schemes", "Discounts, schemes & rebates"],
          ].map(([k, label]) => (
            <div key={k} className="grid grid-cols-[1fr_70px_70px] items-end gap-3">
              <span className="text-sm font-medium text-zinc-800 pb-2">{label} <span className="text-xs text-zinc-400">(% of revenue)</span></span>
              <Field label="Last yr" suffix="%" value={v[`${k}A`]} onChange={set(`${k}A`)} testid={`p01-${k}-a`} />
              <Field label="This yr" suffix="%" value={v[`${k}B`]} onChange={set(`${k}B`)} testid={`p01-${k}-b`} />
            </div>
          ))}
          <Note>Enter each cost once. If an expense already sits inside your COGS, leave its line at zero — double counting is how margin bridges lie.</Note>
        </div>
        <div>
          <div className="border border-zinc-200 bg-white p-6">
            <div className="overline">Contribution bridge</div>
            <div className="mt-3">
              <ResultRow label={`Contribution (${v.labelA || "LY"})`} value={`${inr(r.contribA)} (${r.pctA.toFixed(1)}%)`} />
              <ResultRow label={`Contribution (${v.labelB || "TY"})`} value={`${inr(r.contribB)} (${r.pctB.toFixed(1)}%)`} tone={r.contribB < r.contribA ? "bad" : "good"} />
              <ResultRow strong label="Change" value={`${r.contribB >= r.contribA ? "+" : ""}${inr(r.contribB - r.contribA)}`} tone={r.contribB < r.contribA ? "bad" : "good"} />
            </div>
          </div>
          <ToolSection title="Biggest leaks" testid="p01-leaks">
            <div className="space-y-2">
              {r.leaks.filter((l) => l.rateEffect > 0).slice(0, 3).map((l, i) => (
                <div key={l.label} className="border border-black/10 px-4 py-3">
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm"><span className="overline mr-2">Leak {i + 1}</span>{l.label}</span>
                    <span className="text-sm font-medium tabular-nums text-red-700">−{inr(l.rateEffect)} margin rate</span>
                  </div>
                  {l.volumeEffect > 0 && (
                    <p className="mt-1 text-xs text-zinc-500 tabular-nums">plus {inr(l.volumeEffect)} of volume effect — cost that grew only because revenue grew</p>
                  )}
                </div>
              ))}
              {r.leaks.every((l) => l.rateEffect <= 0) && <p className="text-sm text-zinc-500">No margin-rate leakage entered — cost lines moved only with volume.</p>}
            </div>
          </ToolSection>
          <ToolSection title="The waterfall" testid="p01-waterfall">
            <Waterfall items={r.wf} />
          </ToolSection>
        </div>
      </div>
      <CaseFile />
    </div>
  );
}

// Signature composite case: "Sales Grew 40%, Profit Fell" (fictional, labelled).
const CASE = {
  rows: [
    { label: "Revenue", a: "₹10.0 Cr", b: "₹14.0 Cr" },
    { label: "Growth", a: "—", b: "+40%" },
    { label: "COGS", a: "61%", b: "62%" },
    { label: "Marketplace & channel fees", a: "3%", b: "9%" },
    { label: "Discounts & schemes", a: "4%", b: "8%" },
    { label: "Returns", a: "1%", b: "4%" },
    { label: "Contribution", a: "₹3.1 Cr (31%)", b: "₹2.38 Cr (17%)" },
  ],
  answer: "fees",
  options: [
    { v: "price", label: "They cut prices too deeply" },
    { v: "fees", label: "The channel mix shifted to expensive online channels" },
    { v: "cogs", label: "Raw material costs spiralled" },
    { v: "volume", label: "They sold to the wrong customers" },
  ],
  reveal: "Revenue rose 40% but contribution fell ₹72 L. The largest single leak is channel fees (+6 points ≈ ₹84 L), followed by discounting (+4 points) and returns (+3 points) — all three driven by the same decision: chasing marketplace growth without repricing for marketplace economics. COGS barely moved.",
};

function CaseFile() {
  const [guess, setGuess] = useState(null);
  const [revealed, setRevealed] = useState(false);
  return (
    <ToolSection title="Case file: “Sales Grew 40%, Profit Fell”" testid="p01-case">
      <div className="border border-zinc-200 bg-white p-6 md:p-8">
        <p className="text-xs text-zinc-500 uppercase tracking-wide">Illustrative composite case — not a client engagement</p>
        <p className="mt-3 text-sm text-zinc-700 leading-relaxed max-w-3xl">
          A consumer brand's sales head reported a record year. The CFO reported a worse one. Here is the P&L. Before the reveal: what do you think ate the profit?
        </p>
        <table className="mt-5 w-full max-w-xl text-sm">
          <thead>
            <tr className="text-left text-xs text-zinc-500 border-b border-black/10">
              <th className="py-2 font-medium">Line</th><th className="py-2 font-medium">Year 1</th><th className="py-2 font-medium">Year 2</th>
            </tr>
          </thead>
          <tbody>
            {CASE.rows.map((r) => (
              <tr key={r.label} className="border-b border-black/5">
                <td className="py-2 text-zinc-600">{r.label}</td>
                <td className="py-2 tabular-nums">{r.a}</td>
                <td className="py-2 tabular-nums font-medium">{r.b}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!revealed ? (
          <div className="mt-6">
            <div className="overline">Your call</div>
            <div className="mt-3 grid sm:grid-cols-2 gap-2 max-w-2xl">
              {CASE.options.map((o) => (
                <button
                  key={o.v}
                  type="button"
                  data-testid={`p01-case-${o.v}`}
                  onClick={() => { setGuess(o.v); setRevealed(true); track("tool_case_reveal", { tool: "P01", guess: o.v }); }}
                  className={`text-left border px-4 py-3 text-sm transition-colors ${guess === o.v ? "border-black bg-zinc-50" : "border-zinc-300 hover:border-black"}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-6 border-l-2 border-black pl-4" data-testid="p01-case-reveal">
            <p className="text-sm font-medium">{guess === CASE.answer ? "Correct — and most teams guess wrong." : "A reasonable guess — but the bridge says otherwise."}</p>
            <p className="mt-2 text-sm text-zinc-600 leading-relaxed max-w-3xl">{CASE.reveal}</p>
            <p className="mt-3 text-sm text-zinc-600 leading-relaxed max-w-3xl">
              The fix is rarely "stop growing online". It is repricing for channel economics, governing schemes, and knowing pocket price by channel — which is what the calculator above is for.
            </p>
          </div>
        )}
      </div>
    </ToolSection>
  );
}
